import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { LikeIcon, upArrow } from 'shared-ui/assets'
import { LazyImage,LikeCardShimmer,CommentCardShimmer} from 'shared-ui/components'
import { useTheme } from 'styled-components'
import { useResponsive, useLikesByContentIdAndSearch } from 'shared-ui/hooks'
import { media } from 'shared-ui/utils/media'
import {
  COMMON_MESSAGES,
  ROUTES
} from 'shared-ui/utils/constants'
import { useNavigate, useLocation } from 'react-router-dom'
import { CiHeart  } from "react-icons/ci";
import { IoEyeOutline } from "react-icons/io5";


const BottomSheetList = ({
  open,
  onClose,
  title = '',
  data = [],
  searchPlaceholder = 'Search',
  renderItem,
  isCommentSheet = false,
  onCommentSend,
  commentPending,
  commentInputValue,
  setCommentInputValue,
  selectedPlan = '',
  fetchPriceData = '',
  subscriptionIds = '',
  contentId,
  onEndReached,
  hasNextPage,
  isFetchingNextPage,
  type = '',
  likeCount = 0,
  viewCount = 0,
  initialLoading = false
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [search, setSearch] = useState('')
  const [isClosing, setIsClosing] = useState(false)
  const theme = useTheme()
  const isMobile = useResponsive(media.mobile)
  const [filteredData, setfilteredData] = useState([])

  const likesQuery = useLikesByContentIdAndSearch(contentId, search, {
    enabled: open && !!contentId && !isCommentSheet
  })
  useEffect(() => {
    if (!open) {
      setIsClosing(false)
      setSearch('')
    }
  }, [open])

  const handleClose = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      onClose && onClose()
    }, 300)
  }, [onClose])

  const filteredDataMemo = useMemo(() => {
    if (!Array.isArray(data)) return []

    return data.filter(
      (item) =>
        (item.username || '').toLowerCase().includes(search.toLowerCase()) ||
        (item.comment_text || '').toLowerCase().includes(search.toLowerCase())
    )
  }, [data, search])

  useEffect(() => {
    setfilteredData(filteredDataMemo)
  }, [filteredDataMemo])

  useEffect(() => {
    const updateHeight = () => {
      document.documentElement.style.setProperty(
        '--vh',
        `${window.visualViewport.height * 0.01}px`
      )
    }

    updateHeight()
    window.visualViewport?.addEventListener('resize', updateHeight)

    return () => {
      window.visualViewport?.removeEventListener('resize', updateHeight)
    }
  }, [])

  const getSearchValue = useCallback((e) => {
    setSearch(e.target.value)
  }, [])

  const handleContinue = useCallback(() => {
    handleClose()
    navigate(ROUTES.payment, {
      state: { selectedPlan, fetchPriceData, subscriptionIds }
    })
  }, [selectedPlan, fetchPriceData, subscriptionIds, navigate])

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        if (onEndReached && hasNextPage && !isFetchingNextPage) {
          onEndReached()
        }
      }
    },
    [onEndReached, hasNextPage, isFetchingNextPage]
  )

  if (!open) return null

  const actions = [
    {
      id: 'likes',
      icon: <CiHeart size={20} color={theme.app.iconlabel}/>,
      count: likeCount
    },
    {
      id: 'views',
      icon: <IoEyeOutline size={20} color={theme.app.iconlabel}/>,
      count: viewCount
    }
  ]
  return (
    <div
      className={`fixed inset-0 z-50 flex transition-opacity duration-300 bg-black ${
        isClosing ? 'bg-opacity-0' : 'bg-opacity-50'
      } ${isMobile ? 'items-end justify-center' : 'items-center justify-end'}`}
      style={{ height: 'calc(var(--vh, 1vh) * 100)' }}
      onClick={handleClose}
    >
      <div
        className={`${
          isMobile
            ? `w-[96vw] max-w-md mx-auto border min-h-[60vh] max-h-[85vh] border-${theme.app.iconlabel} rounded-t-2xl pt-4 pb-8`
            : `w-[420px] h-full border-l border-${theme.app.iconlabel} pt-4 `
        } relative flex flex-col shadow-lg ml-auto ${
          isClosing
            ? ''
            : isMobile
              ? 'animate-slideInUp'
              : 'animate-slideInRight'
        }`}
        style={{
          background: theme.app.background,
          transform: isClosing
            ? isMobile
              ? 'translateY(100%)'
              : 'translateX(100%)'
            : 'translate(0, 50px)',
          transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          paddingBottom: isMobile
            ? `calc(56px + env(safe-area-inset-bottom))`
            : '65px',
          zIndex: 60
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        {isMobile && (
          <div className="w-12 h-1 bg-gray-700 rounded-full mx-auto mb-4" />
        )}
        {/* Header */}
        <div className="flex items-center justify-between px-8 mb-6">
          <h2
            className={`text-2xl font-normal`}
            style={{ color: theme.app.text }}
          >
            {title}
          </h2>
          <button
            className={` text-3xl`}
            style={{ color: theme.app.text }}
            onClick={handleClose}
          >
            ×
          </button>
         
        </div>
         {type === 'Likes' && (
            <div className="flex items-center mb-4 px-8">
              {actions.map((item) => (
                <div key={item.id} className="flex items-center mr-4">
                  {item.icon}
                  <span
                    className="ml-1"
                    style={{ color: theme.app.iconlabel, fontSize: '14px' }}
                  >
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          )}

        {(location.pathname === ROUTES.subscription ||
          location.pathname === ROUTES.subscriptionSettingPlan) && (
          <div className="px-7 overflow-y-auto max-h-[70vh] pb-24">
            {renderItem}
          </div>
        )}

        {/* Like count and search bar for likes only */}
        {location.pathname !== ROUTES.subscription ||
          (location.pathname !== ROUTES.subscriptionSettingPlan &&
            !isCommentSheet && (
              <>
                <div className="flex items-center space-x-6 px-7 mb-4">
                  <div className="flex items-center space-x-1 text-gray-300">
                    <LazyImage src={LikeIcon} alt="likeicon" />
                    <span>{filteredData.length ?? 0}</span>
                  </div>
                </div>
                <div className="flex px-4 mb-4">
                  <input
                    className="w-full px-4 h-12 bg-transparent py-2 border border-gray-700 rounded focus:outline-none"
                    style={{
                      background: theme.app.background,
                      color: theme.app.text
                    }}
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => getSearchValue(e)}
                  />
                </div>
              </>
            ))}
        {/* List */}
        {location.pathname !== ROUTES.subscriptionSettingPlan && (
          <div
            className={` ${isMobile ? 'px-6' : 'px-8'} flex-1 overflow-y-auto`}
            style={{ maxHeight: isMobile ? '45dvh' : undefined }}
            onScroll={handleScroll}
          >
            {initialLoading || likesQuery.isLoading ? (
              isCommentSheet?Array.from({ length: 10 }).map((_, index) => (<CommentCardShimmer key={index} className="flex gap-3 py-3" />)):
              Array.from({ length: 10 }).map((_, index) => (<LikeCardShimmer key={index} className="flex items-center justify-between py-3 gap-2 cursor-pointer" />))

            ) : filteredData.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                No {title.toLowerCase()} found.
              </div>
            ) : (
              <>
                {filteredData && filteredData.map(renderItem)}
               
                {isFetchingNextPage && (
                  <div className="text-center text-gray-400 py-4 italic">
                    {COMMON_MESSAGES.Loading}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {/* Emoji row and comment input for comments */}
        {(location.pathname !== ROUTES.subscription ||
          location.pathname !== ROUTES.subscriptionSettingPlan) &&
          isCommentSheet && (
            <>
              <div
                className="px-6 pt-2 "
                style={{
                  paddingBottom: isMobile ? '12px' : undefined,
                  position: isMobile ? 'sticky' : undefined,
                  bottom: isMobile
                    ? `calc(12px + env(safe-area-inset-bottom))`
                    : undefined,
                  zIndex: isMobile ? 70 : undefined,
                  background: isMobile ? theme.app.background : undefined
                }}
              >
                <div
                  className={`flex items-center gap-4 px-4 py-3 rounded border border-gray-700`}
                  style={{ background: theme.app.background }}
                >
                  <input
                    className={`w-full bg-transparent rounded focus:outline-none placeholder:text-${theme.app.text} placeholder:text-sm`}
                    placeholder="Add comment"
                    value={commentInputValue || ''}
                    onChange={(e) =>
                      setCommentInputValue &&
                      setCommentInputValue(e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !commentPending && onCommentSend) {
                        onCommentSend()
                      }
                    }}
                    style={{ color: theme.app.text }}
                  />

                  <div className="cursor-pointer">
                    <img
                      src={upArrow}
                      className="w-4 h-4 cursor-pointer"
                      alt="Submit"
                      onClick={() => {
                       if(!commentPending && onCommentSend) onCommentSend()
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        <style>{`
                    .animate-slideInUp { animation: slideInUp 0.3s cubic-bezier(0.4,0,0.2,1); }
                    .animate-slideInRight { animation: slideInRight 0.3s cubic-bezier(0.4,0,0.2,1); }
                    @keyframes slideInUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
                    @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
                `}</style>
      </div>
    </div>
  )
}

export default React.memo(BottomSheetList)
